// Экраны кладут сюда функции «загрузи свои данные заново». Пока сокет лежал,
// события прошли мимо, поэтому после восстановления связи вызываем их все.
// Пробный проход сначала спрашивает у бэка версию выборки каждого экрана и зовёт
// обработчики, только если версия изменилась.
//
// Без импортов и в CommonJS, чтобы тест запускался голым node, как у
// kkm-server/pipeline-core.js.

// Сколько ждём один обработчик. Меньше минуты сторожа, чтобы зависший запрос
// не съел его следующий проход
const HANDLER_TIMEOUT_MS = 30000
// Пауза сторожа отсчитывается от последнего признака жизни: пакета события по сокету
// или конца прохода. При живом сокете события сами держат данные свежими
const WATCHDOG_CONNECTED_MS = 300000
// При лежащем сокете - раз в 30 секунд (владелец, 2026-10-22), как обещает полоса
const WATCHDOG_DISCONNECTED_MS = 30000
// После провала пробы повторяем быстрее, чтобы «Сервер недоступен» появился за минуту
const WATCHDOG_RETRY_MS = 20000
// Страховочный полный проход без проверки версии: правки только в корзине или
// платеже не двигают orders.updated_at
const FULL_PASS_MS = 600000
// Вкладка скрыта короче - события дошли и в фоне, перечитывать нечего (владелец, 2026-10-21)
const HIDDEN_THRESHOLD_MS = 60000
// Разброс прохода после реконнекта: все вкладки не должны перечитывать данные разом
const RECONNECT_JITTER_MAX_MS = 10000
// Столько проб сторожа подряд без ответа сервера - полоса «Сервер недоступен»
const SERVER_FAILURES_TO_ALARM = 3

// Можно ли просто дождаться уже идущего обновления вместо нового. Нельзя, если
// идущее «тише» (без тостов, а пользователь нажал «Обновить» и ждёт ошибку),
// «уже» (сторож пропускает биллинг, а нужен полный проход), только проверяет
// версию (а нужен полный проход) или не принудительное (а кнопка ждёт данных)
function covers (running, requested) {
  return (!running.silent || requested.silent) &&
    (!running.watchdog || requested.watchdog) &&
    (!running.probe || requested.probe) &&
    (!requested.force || running.force)
}

// Сервер «не ответил»: сетевая ошибка, таймаут (ответа нет) или 5xx. Отмена axios и 4xx -
// ответ есть, сервер жив
function defaultIsServerFailure (error) {
  if (!error || error.__CANCEL__) {
    return false
  }
  if (!error.response) {
    return true
  }
  return error.response.status >= 500
}

function createRefreshRegistry ({
  ensureConnected,
  fetchVersion = () => Promise.reject(new Error('refresh: fetchVersion is not configured')),
  isServerFailure = defaultIsServerFailure,
  onServerUnavailable = () => {},
  warn = console.warn,
  handlerTimeoutMs = HANDLER_TIMEOUT_MS,
  now = Date.now,
  random = Math.random,
}) {
  const handlers = new Map()
  let current = null
  // Сторож отсчитывает паузу от последнего признака жизни, а не по своему таймеру:
  // иначе сразу после восстановления связи он повторял бы те же запросы.
  // Старт - момент загрузки: экраны в это время грузят данные сами
  const startedAt = now()
  let finishedAt = startedAt
  let fullFinishedAt = startedAt
  let aliveAt = startedAt
  let lastAttemptAt = startedAt
  let failures = 0
  let unavailable = false

  // После сна ноутбука или смены Wi-Fi запрос может висеть без ответа минутами.
  // Пока он висит, обновление не закончится, и сторож с кнопкой «Обновить» ждут
  function withTimeout (promise) {
    let timer
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new Error(`refresh: handler timeout ${handlerTimeoutMs} ms`)), handlerTimeoutMs)
    })

    return Promise.race([promise, timeout]).then(
      (value) => {
        clearTimeout(timer)
        return value
      },
      (error) => {
        clearTimeout(timer)
        throw error
      },
    )
  }

  function selectProbes () {
    const all = Array.from(handlers.values())
      .filter((options) => options.probe)
      .map((options) => ({ options, params: options.probe() }))
      .filter((probe) => probe.params)
    const regular = all.filter((probe) => !probe.options.fallback)
    return regular.length ? regular : all
  }

  // Запрашивает версии всех проб. changed - хоть одна версия отличается от запомненной,
  // упала или проб нет; unanswered - все пробы упали по вине сервера (лежащий бэк
  // не грузим ещё и списками); results - ответы для запоминания после полного прохода
  async function probeVersions () {
    const probes = selectProbes()
    if (!probes.length) {
      return { changed: true, unanswered: false, results: [] }
    }

    const results = await Promise.all(probes.map((probe) =>
      withTimeout(Promise.resolve().then(() => fetchVersion(probe.params))).then(
        (version) => ({ probe, version, before: probe.options.version }),
        (error) => {
          warn('refresh: version probe failed', error)
          return { probe, error }
        },
      ),
    ))

    const changed = results.some((r) => r.error || r.version !== r.probe.options.version)
    const unanswered = results.every((r) => r.error && isServerFailure(r.error))
    return { changed, unanswered, results }
  }

  // Проба без ответа - все запросы версии упали по вине сервера. Провалы копятся только у
  // сторожа и кнопки (counted), успех обнуляет счётчик у любого прохода
  function recordProbeOutcome ({ results, unanswered }, counted) {
    if (!results.length) {
      return
    }

    if (!unanswered) {
      failures = 0
      if (unavailable) {
        unavailable = false
        onServerUnavailable(false)
      }
      return
    }

    if (!counted) {
      return
    }
    failures++
    if (failures >= SERVER_FAILURES_TO_ALARM && !unavailable) {
      unavailable = true
      onServerUnavailable(true)
    }
  }

  // После полного прохода запоминаем версии проб. Если экран за это время сообщил свою
  // через setVersion, его версия точнее
  function rememberVersions (results) {
    results.forEach((r) => {
      if (!r.error && r.probe.options.version === r.before) {
        r.probe.options.version = r.version
      }
    })
  }

  async function execute ({ silent, watchdog, probe, force }) {
    // Данные идут по HTTP, сокет им не нужен: не подключились - всё равно перечитываем
    await Promise.resolve()
      .then(ensureConnected)
      .catch((e) => warn('refresh: ensureConnected failed', e))

    let probed = null
    if (probe) {
      probed = await probeVersions()
      recordProbeOutcome(probed, watchdog || force)
      // Сервер не ответил на пробу - полный проход только добавит ему запросов, а данных
      // не даст. Кнопку это тоже касается: полоса уже говорит, что сервер недоступен
      if (probed.unanswered || (!probed.changed && !force)) {
        return
      }
    }

    const selected = Array.from(handlers)
      .filter(([, options]) => !watchdog || options.watchdog)
      .map(([fn]) => fn)

    await Promise.all(selected.map((fn) => withTimeout(Promise.resolve().then(() => fn({ silent })))
      .catch((e) => warn('refresh: handler failed', e))))

    fullFinishedAt = now()
    if (probed) {
      rememberVersions(probed.results)
    }
  }

  function run ({ silent = false, watchdog = false, probe = false, force = false } = {}) {
    const requested = { silent, watchdog, probe, force }

    if (current && covers(current, requested)) {
      return current.promise
    }

    // Идущее не подходит - встаём в очередь за ним
    if (current) {
      return current.promise.then(() => run(requested))
    }

    if (watchdog) {
      lastAttemptAt = now()
    }

    const promise = execute(requested).then(() => {
      current = null
      finishedAt = now()
    })
    current = { ...requested, promise }

    return promise
  }

  return {
    // probe - функция, возвращающая параметры версии экрана ({ filialId, date, filterParam })
    // или null, если проверять сейчас нечего. fallback - запасная проба, идёт в дело только
    // когда ни одна обычная проба параметров не дала
    register (fn, { watchdog = true, probe = null, fallback = false } = {}) {
      const existing = handlers.get(fn)
      handlers.set(fn, { watchdog, probe, fallback, version: existing ? existing.version : null })
    },
    // Экран сам загрузил данные и знает их версию из meta.version: без этого сторож
    // счёл бы свежезагруженный список изменившимся
    setVersion (fn, version) {
      const options = handlers.get(fn)
      if (options) {
        options.version = version
      }
    },
    unregister (fn) {
      handlers.delete(fn)
    },
    run,
    isRunning () {
      return current !== null
    },
    msSinceRefresh () {
      return now() - finishedAt
    },
    markAlive () {
      aliveAt = now()
    },
    msSinceAlive () {
      return now() - aliveAt
    },
    // Решение тика сторожа. 'full' - страховочный полный проход, 'probe' - проверка версии,
    // null - ничего. Скрытую вкладку перечитает visibilitychange при возврате
    watchdogAction ({ visible, wsConnected }) {
      if (!visible || current) {
        return null
      }
      const t = now()
      if (t - fullFinishedAt >= FULL_PASS_MS) {
        return 'full'
      }
      if (failures > 0) {
        return t - lastAttemptAt >= WATCHDOG_RETRY_MS ? 'probe' : null
      }
      const quietMs = t - Math.max(aliveAt, finishedAt)
      const limit = wsConnected ? WATCHDOG_CONNECTED_MS : WATCHDOG_DISCONNECTED_MS
      return quietMs >= limit ? 'probe' : null
    },
    reconnectDelayMs () {
      return random() * RECONNECT_JITTER_MAX_MS
    },
  }
}

module.exports = {
  createRefreshRegistry,
  WATCHDOG_CONNECTED_MS,
  WATCHDOG_DISCONNECTED_MS,
  WATCHDOG_RETRY_MS,
  FULL_PASS_MS,
  HIDDEN_THRESHOLD_MS,
  RECONNECT_JITTER_MAX_MS,
  SERVER_FAILURES_TO_ALARM,
}
